import { useState } from 'react'
import { ChevronRightIcon, CircleHelp, PencilIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type {
  PlrAssessment13849,
  SafetyFunctionPlrUndeterminedReason,
  SafetyFunctionWithArchitecture,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { PlrCircleBadge } from '../../components/PlrCircleBadge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { EditSafetyFunctionSelectFieldDialog } from './EditSafetyFunctionSelectFieldDialog'
import { EditSafetyFunctionTextFieldDialog } from './EditSafetyFunctionTextFieldDialog'
import type { SafetyFunctionFormData } from './safetyFunctionForm.schema'
import { EnIso13849RiskGraph } from './EnIso13849RiskGraph'
import { hazardDetailLinkStateFromSafetyFunction } from '../hazards/hazardDetailNavigation'
import {
  getOptionalFieldDisplayValue,
  getSafetyFunctionSourceLabel,
  toSafetyFunctionFormData,
} from './safetyFunctionsUtils'

type SafetyFunctionDetailInfoWidgetsProps = {
  safetyFunction: SafetyFunctionWithArchitecture
  isUpdating: boolean
  onUpdate: (data: SafetyFunctionFormData, onSuccess?: () => void) => void
}

type EditableTextField = Exclude<
  keyof SafetyFunctionFormData,
  'name' | 'plcal' | 'plrdet'
>

type EditableSelectField = 'plcal' | 'plrdet'

type TextFieldConfig = {
  field: EditableTextField
  labelKey: string
  multiline?: boolean
  className?: string
}

const IDENTITY_TEXT_FIELDS: TextFieldConfig[] = [
  {
    field: 'sfid',
    labelKey: 'safetyFunctionsPage.srsFields.sfid',
  },
  {
    field: 'sftype',
    labelKey: 'safetyFunctionsPage.srsFields.sftype',
  },
  {
    field: 'description',
    labelKey: 'safetyFunctionsPage.createDialog.description',
    multiline: true,
    className: 'sm:col-span-2',
  },
]

const BEHAVIOUR_TEXT_FIELDS: TextFieldConfig[] = [
  {
    field: 'triggerevent',
    labelKey: 'safetyFunctionsPage.srsFields.triggerevent',
  },
  {
    field: 'reaction',
    labelKey: 'safetyFunctionsPage.srsFields.reaction',
  },
  {
    field: 'safestate',
    labelKey: 'safetyFunctionsPage.srsFields.safestate',
  },
  {
    field: 'responsetime',
    labelKey: 'safetyFunctionsPage.srsFields.responsetime',
  },
  {
    field: 'opmode',
    labelKey: 'safetyFunctionsPage.srsFields.opmode',
  },
  {
    field: 'requestfrequency',
    labelKey: 'safetyFunctionsPage.srsFields.requestfrequency',
  },
]

const PERFORMANCE_TEXT_FIELDS: TextFieldConfig[] = [
  {
    field: 'sfinterfaces',
    labelKey: 'safetyFunctionsPage.srsFields.sfinterfaces',
    multiline: true,
    className: 'sm:col-span-2',
  },
  {
    field: 'priority',
    labelKey: 'safetyFunctionsPage.srsFields.priority',
  },
]

const ALL_TEXT_FIELDS: TextFieldConfig[] = [
  ...IDENTITY_TEXT_FIELDS,
  ...BEHAVIOUR_TEXT_FIELDS,
  ...PERFORMANCE_TEXT_FIELDS,
]

function getEnumDisplayValue(
  value: string | null | undefined,
  enumPrefix: 'plrdet',
  naLabel: string,
  translate: (key: string) => string
): string {
  if (value == null || value.trim() === '') {
    return naLabel
  }
  return translate(`safetyFunctionsPage.enums.${enumPrefix}.${value}`)
}

function getHazardTypeLabel(
  type: string | null,
  naLabel: string,
  translate: (key: string) => string
): string {
  if (!type) {
    return naLabel
  }
  const key = `hazardsPage.enums.hazardTypes.${type}`
  const translated = translate(key)
  return translated === key ? type : translated
}

function getPlr13849DimensionLabel(
  category: 'severity' | 'frequency' | 'possibility',
  value: string,
  translate: (key: string) => string
): string {
  return translate(`safetyFunctionsPage.sfDetail.plr13849.${category}.${value}`)
}

function getDeterminingHazardLabel(
  plrAssessment: PlrAssessment13849,
  naLabel: string,
  translate: (key: string) => string
): string {
  const hazardTypeLabel = getHazardTypeLabel(
    plrAssessment.determiningHazard.type,
    naLabel,
    translate
  )
  const description = plrAssessment.determiningHazard.description?.trim()
  if (description != null && description.length > 0) {
    return `${hazardTypeLabel} — ${description}`
  }
  return hazardTypeLabel
}

type PlrAssessmentDetailsProps = {
  plrAssessment: PlrAssessment13849
  safetyFunctionId: string
  naLabel: string
  translate: (key: string) => string
}

function PlrAssessmentDetails({
  plrAssessment,
  safetyFunctionId,
  naLabel,
  translate,
}: PlrAssessmentDetailsProps) {
  const determiningHazardLabel = getDeterminingHazardLabel(
    plrAssessment,
    naLabel,
    translate
  )

  return (
    <dl className='mt-2 min-w-0 space-y-1 text-sm text-muted-foreground'>
      <div className='flex flex-wrap gap-x-2 gap-y-0.5'>
        <dt className='font-medium text-foreground'>
          {translate('safetyFunctionsPage.sfDetail.plrSeverityLabel')}
        </dt>
        <dd>
          {getPlr13849DimensionLabel(
            'severity',
            plrAssessment.severity,
            translate
          )}
        </dd>
      </div>
      <div className='flex flex-wrap gap-x-2 gap-y-0.5'>
        <dt className='font-medium text-foreground'>
          {translate('safetyFunctionsPage.sfDetail.plrFrequencyLabel')}
        </dt>
        <dd>
          {getPlr13849DimensionLabel(
            'frequency',
            plrAssessment.frequency,
            translate
          )}
        </dd>
      </div>
      <div className='flex flex-wrap gap-x-2 gap-y-0.5'>
        <dt className='font-medium text-foreground'>
          {translate('safetyFunctionsPage.sfDetail.plrPossibilityLabel')}
        </dt>
        <dd>
          {getPlr13849DimensionLabel(
            'possibility',
            plrAssessment.possibilityOfAvoidance,
            translate
          )}
        </dd>
      </div>
      <div className='pt-1'>
        <dt className='font-medium text-foreground'>
          <span className='inline-flex items-center gap-1'>
            {translate('safetyFunctionsPage.sfDetail.plrDeterminingHazardLabel')}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  className='inline-flex text-muted-foreground/70 hover:text-muted-foreground'
                  aria-label={translate(
                    'safetyFunctionsPage.sfDetail.plrDeterminingHazardTooltip'
                  )}
                >
                  <CircleHelp className='size-3.5' aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side='top' className='max-w-sm text-left'>
                {translate(
                  'safetyFunctionsPage.sfDetail.plrDeterminingHazardTooltip'
                )}
              </TooltipContent>
            </Tooltip>
          </span>
        </dt>
        <dd className='mt-0.5 flex items-start gap-1'>
          <span className='min-w-0 flex-1 break-words'>
            {determiningHazardLabel}
          </span>
          <Link
            relative='route'
            to={`../hazards/${plrAssessment.determiningHazard.id}`}
            state={hazardDetailLinkStateFromSafetyFunction(safetyFunctionId)}
            className='inline-flex size-8 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground'
            aria-label={translate(
              'safetyFunctionsPage.sfDetail.viewDeterminingHazardAria'
            )}
          >
            <ChevronRightIcon className='size-4' />
          </Link>
        </dd>
      </div>
    </dl>
  )
}

type PlrUndeterminedGuidanceProps = {
  reasons: SafetyFunctionPlrUndeterminedReason[]
  translate: (key: string) => string
}

function PlrUndeterminedGuidance({
  reasons,
  translate,
}: PlrUndeterminedGuidanceProps) {
  return (
    <Alert variant='info' className='mt-2'>
      <AlertTitle>
        {translate('safetyFunctionsPage.sfDetail.plrUndetermined.title')}
      </AlertTitle>
      <AlertDescription className='space-y-3'>
        <p>{translate('safetyFunctionsPage.sfDetail.plrUndetermined.guidance')}</p>
        <ul className='list-disc space-y-1 pl-5'>
          {reasons.length > 0 &&
            reasons.map((reason) => (
              <li key={reason}>
                {translate(
                  `safetyFunctionsPage.sfDetail.plrUndetermined.reasons.${reason}`
                )}
              </li>
            ))}
          {reasons.length === 0 && (
            <li>
              {translate(
                'safetyFunctionsPage.sfDetail.plrUndetermined.fallbackReason'
              )}
            </li>
          )}
        </ul>
        <Button variant='outline' size='sm' asChild>
          <Link relative='route' to='../risk-reduction-measures'>
            {translate(
              'safetyFunctionsPage.sfDetail.plrUndetermined.openRiskReductionMeasures'
            )}
          </Link>
        </Button>
      </AlertDescription>
    </Alert>
  )
}

export function SafetyFunctionDetailInfoWidgets({
  safetyFunction,
  isUpdating,
  onUpdate,
}: SafetyFunctionDetailInfoWidgetsProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [editingTextField, setEditingTextField] =
    useState<EditableTextField | null>(null)
  const [editingSelectField, setEditingSelectField] =
    useState<EditableSelectField | null>(null)

  const naLabel = t('hazardsPage.hazardDetail.na')
  const formData = toSafetyFunctionFormData(safetyFunction)
  const showEplanReference = safetyFunction.source === 'EPLAN'
  const plrAssessment = safetyFunction.plrAssessment ?? null
  const hasPlrAssessment = plrAssessment != null
  const plrUndeterminedReasons = safetyFunction.plrUndeterminedReasons ?? []
  const showPlrUndeterminedGuidance =
    safetyFunction.plr == null && !hasPlrAssessment
  const showPlrCircleBadge = hasPlrAssessment
  let plrItemClassName: string | undefined
  if (hasPlrAssessment || showPlrUndeterminedGuidance) {
    plrItemClassName = 'sm:col-span-2'
  }

  const handleTextFieldSave = (data: SafetyFunctionFormData) => {
    onUpdate(data, () => {
      setEditingTextField(null)
    })
  }

  const handleSelectFieldSave = (data: SafetyFunctionFormData) => {
    onUpdate(data, () => {
      setEditingSelectField(null)
    })
  }

  const renderTextFieldItem = ({
    field,
    labelKey,
    multiline = false,
    className,
  }: TextFieldConfig) => {
    const fieldLabel = t(labelKey)
    const rawValue = formData[field]
    const displayValue = getOptionalFieldDisplayValue(rawValue, naLabel)

    return (
      <Item
        key={field}
        variant='outline'
        size={multiline ? 'xs' : undefined}
        className={className}
      >
        <ItemContent>
          <ItemTitle>{fieldLabel}</ItemTitle>
          <ItemDescription
            className={multiline ? 'whitespace-pre-wrap' : undefined}
          >
            {displayValue}
          </ItemDescription>
        </ItemContent>
        <ItemActions>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='size-8 shrink-0 text-muted-foreground'
            onClick={() => setEditingTextField(field)}
            disabled={isUpdating}
            aria-label={t('safetyFunctionsPage.sfDetail.editFieldAria', {
              field: fieldLabel,
            })}
          >
            <PencilIcon className='size-4' />
          </Button>
        </ItemActions>
      </Item>
    )
  }

  return (
    <>
      <div className='space-y-4'>
        <section className='space-y-2'>
          <h3 className='text-base font-semibold'>
            {t('safetyFunctionsPage.srsGroups.identity')}
          </h3>
          <div className='grid gap-2 sm:grid-cols-2'>
            <Item variant='outline'>
              <ItemContent>
                <ItemTitle>
                  {t('safetyFunctionsPage.sfDetail.sourceLabel')}
                </ItemTitle>
                <ItemDescription>
                  {getSafetyFunctionSourceLabel(safetyFunction.source, t)}
                </ItemDescription>
              </ItemContent>
            </Item>

            {showEplanReference && (
              <Item variant='outline'>
                <ItemContent>
                  <ItemTitle>
                    {t(
                      'safetyFunctionsPage.extractFromEplanModal.referenceIdLabel'
                    )}
                  </ItemTitle>
                  <ItemDescription>
                    {getOptionalFieldDisplayValue(
                      safetyFunction.eplanReferenceId,
                      naLabel
                    )}
                  </ItemDescription>
                </ItemContent>
              </Item>
            )}

            {IDENTITY_TEXT_FIELDS.map(renderTextFieldItem)}
          </div>
        </section>

        <section className='space-y-3'>
          <h3 className='text-base font-semibold'>
            {t('safetyFunctionsPage.srsGroups.behaviour')}
          </h3>
          <div className='grid gap-2 sm:grid-cols-3'>
            {BEHAVIOUR_TEXT_FIELDS.map(renderTextFieldItem)}
          </div>
        </section>

        <section className='space-y-3'>
          <h3 className='text-base font-semibold'>
            {t('safetyFunctionsPage.srsGroups.performance')}
          </h3>
          <div className='grid gap-2 sm:grid-cols-2'>
            {PERFORMANCE_TEXT_FIELDS.map(renderTextFieldItem)}

            <Item variant='outline' size='sm' className={plrItemClassName}>
              <ItemContent className='min-w-0'>
                <ItemTitle>{t('safetyFunctionsPage.srsFields.plr')}</ItemTitle>
                {hasPlrAssessment && (
                  <ItemDescription>
                    {t('safetyFunctionsPage.sfDetail.plrComputedHelper')}
                  </ItemDescription>
                )}
                {showPlrUndeterminedGuidance && (
                  <PlrUndeterminedGuidance
                    reasons={plrUndeterminedReasons}
                    translate={t}
                  />
                )}
                {hasPlrAssessment && (
                  <>
                    <div className='mt-3'>
                      <EnIso13849RiskGraph
                        assessment={plrAssessment}
                        ariaLabel={t(
                          'safetyFunctionsPage.sfDetail.plrRiskGraphAria'
                        )}
                      />
                    </div>
                    <PlrAssessmentDetails
                      plrAssessment={plrAssessment}
                      safetyFunctionId={safetyFunction.id}
                      naLabel={naLabel}
                      translate={t}
                    />
                  </>
                )}
              </ItemContent>
              {showPlrCircleBadge && (
                <ItemActions>
                  <PlrCircleBadge plr={safetyFunction.plr} />
                </ItemActions>
              )}
            </Item>

            <Item variant='outline' size='sm'>
              <ItemContent>
                <ItemTitle>{t('safetyFunctionsPage.srsFields.plcal')}</ItemTitle>
              </ItemContent>
              <ItemActions>
                <PlrCircleBadge plr={safetyFunction.plcal} />
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-8 shrink-0 text-muted-foreground'
                  onClick={() => setEditingSelectField('plcal')}
                  disabled={isUpdating}
                  aria-label={t('safetyFunctionsPage.sfDetail.editFieldAria', {
                    field: t('safetyFunctionsPage.srsFields.plcal'),
                  })}
                >
                  <PencilIcon className='size-4' />
                </Button>
              </ItemActions>
            </Item>

            <Item variant='outline'>
              <ItemContent>
                <ItemTitle>
                  {t('safetyFunctionsPage.srsFields.plrdet')}
                </ItemTitle>
                <ItemDescription>
                  {getEnumDisplayValue(
                    safetyFunction.plrdet,
                    'plrdet',
                    naLabel,
                    t
                  )}
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-8 shrink-0 text-muted-foreground'
                  onClick={() => setEditingSelectField('plrdet')}
                  disabled={isUpdating}
                  aria-label={t('safetyFunctionsPage.sfDetail.editFieldAria', {
                    field: t('safetyFunctionsPage.srsFields.plrdet'),
                  })}
                >
                  <PencilIcon className='size-4' />
                </Button>
              </ItemActions>
            </Item>
          </div>
        </section>
      </div>

      {editingTextField != null ? (
        <EditSafetyFunctionTextFieldDialog
          isOpen
          onClose={() => setEditingTextField(null)}
          safetyFunction={safetyFunction}
          field={editingTextField}
          labelKey={
            ALL_TEXT_FIELDS.find((config) => config.field === editingTextField)
              ?.labelKey ?? ''
          }
          multiline={
            ALL_TEXT_FIELDS.find((config) => config.field === editingTextField)
              ?.multiline
          }
          onSave={handleTextFieldSave}
          isSaving={isUpdating}
        />
      ) : null}

      {editingSelectField === 'plcal' ? (
        <EditSafetyFunctionSelectFieldDialog
          isOpen
          onClose={() => setEditingSelectField(null)}
          safetyFunction={safetyFunction}
          field='plcal'
          labelKey='safetyFunctionsPage.srsFields.plcal'
          onSave={handleSelectFieldSave}
          isSaving={isUpdating}
        />
      ) : null}

      {editingSelectField === 'plrdet' ? (
        <EditSafetyFunctionSelectFieldDialog
          isOpen
          onClose={() => setEditingSelectField(null)}
          safetyFunction={safetyFunction}
          field='plrdet'
          labelKey='safetyFunctionsPage.srsFields.plrdet'
          onSave={handleSelectFieldSave}
          isSaving={isUpdating}
        />
      ) : null}
    </>
  )
}
